import { Box, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

type Evidence = {
  decisionId: string | null;
  date: string | null;
  module: string | null;
  facilityName: string | null;
  excerpt: string | null;
};

type ParsedProvenance = {
  cupaCode: string | null;
  scope: string | null;
  supportingCount: number | null;
  recency: string | null;
  evidence: Evidence[];
};

// Duplicated from encamp-rule-suggestions deliberately — not worth pulling a server package into the browser bundle for a 3-line predicate.
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseEvidence(raw: unknown): Evidence {
  if (!isJsonObject(raw)) {
    return { decisionId: null, date: null, module: null, facilityName: null, excerpt: null };
  }
  return {
    decisionId: typeof raw.decisionId === "string" ? raw.decisionId : null,
    date: typeof raw.date === "string" ? raw.date : null,
    module: typeof raw.module === "string" ? raw.module : null,
    facilityName: typeof raw.facilityName === "string" ? raw.facilityName : null,
    excerpt: typeof raw.excerpt === "string" ? raw.excerpt : null,
  };
}

function parseProvenance(provenance: unknown): ParsedProvenance {
  if (!isJsonObject(provenance)) {
    return { cupaCode: null, scope: null, supportingCount: null, recency: null, evidence: [] };
  }
  const evidence = Array.isArray(provenance.evidence) ? provenance.evidence.map(parseEvidence) : [];
  return {
    cupaCode: typeof provenance.cupaCode === "string" ? provenance.cupaCode : null,
    scope: typeof provenance.scope === "string" ? provenance.scope : null,
    supportingCount:
      typeof provenance.supportingCount === "number" ? provenance.supportingCount : null,
    recency: typeof provenance.recency === "string" ? provenance.recency : null,
    evidence,
  };
}

export function CersCupaPreferenceProvenance({ provenance }: { provenance: unknown }) {
  const parsed = parseProvenance(provenance);
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Evidence</Typography>
      {parsed.supportingCount != null ? (
        <Typography variant="body2" color="text.secondary">
          {parsed.supportingCount} supporting decisions
          {parsed.recency ? ` · ${parsed.recency}` : ""}
          {parsed.scope ? ` · ${parsed.scope}` : ""}
        </Typography>
      ) : null}
      {parsed.evidence.map((e, i) => (
        <Box key={e.decisionId ?? i} sx={{ pl: 1, borderLeft: 2, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">
            {e.date ?? "unknown date"}
            {e.module ? ` · ${e.module}` : ""}
            {e.facilityName ? ` · ${e.facilityName}` : ""}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {e.excerpt ?? ""}
          </Typography>
        </Box>
      ))}
      {parsed.cupaCode ? (
        <MuiLink component={RouterLink} to={`/staff/cers/cupas/${parsed.cupaCode}`}>
          View full CUPA playbook
        </MuiLink>
      ) : null}
    </Stack>
  );
}
